import { useState } from "react";
import { Download, Undo2, Gavel } from "lucide-react";
import { toast } from "sonner";
import { api, download, errMsg, fmtL, fmtTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, useConfirm } from "@/components/opl/Common";
import { useList, DataTable, GhostBtn, IconBtn, GoldBtn } from "@/components/opl/Crud";
import { ManualSaleDialog } from "@/components/opl/ManualSale";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BS = { accepted: "text-emerald-300 border-emerald-500/40", rejected: "text-red-300 border-red-500/40", voided: "text-slate-400" };

export default function BidHistory() {
  const { can } = useAuth();
  const [bids] = useList("/reports/bids");
  const [sales, loadSales] = useList("/sales");
  const [ledger] = useList("/ledger");
  const [dialog, confirm] = useConfirm();
  const [manual, setManual] = useState(false);
  const exp = (p, n) => (f) => download(`${p}${p.includes("?") ? "&" : "?"}fmt=${f}`, `${n}.${f}`).catch((e) => toast.error(errMsg(e)));
  const Ex = ({ fn, id }) => <div className="flex gap-2 mb-3">{["csv", "xlsx", "pdf"].map((f) => <GhostBtn key={f} testid={`export-${id}-${f}`} onClick={() => fn(f)}><Download className="h-4 w-4" />{f.toUpperCase()}</GhostBtn>)}</div>;
  return (
    <div>
      {dialog}
      <ManualSaleDialog open={manual} onOpenChange={setManual} onDone={loadSales} />
      <PageHeader title="Bid History & Ledger" crumbs={["Bid History"]}>{can("reverse_sale") && <GoldBtn testid="open-manual-sale-btn" onClick={() => setManual(true)}><Gavel className="h-4 w-4" />Manual sale</GoldBtn>}</PageHeader>
      <Tabs defaultValue="bids">
        <TabsList className="bg-[#0B1323]"><TabsTrigger value="bids" data-testid="tab-bids">All bids</TabsTrigger><TabsTrigger value="sales" data-testid="tab-sales">Sales</TabsTrigger><TabsTrigger value="ledger" data-testid="tab-ledger">Event ledger</TabsTrigger></TabsList>
        <TabsContent value="bids">
          <Ex fn={exp("/reports/bids", "opl_bids")} id="bids" />
          <DataTable rows={bids} testid="bids-table" columns={[
            { key: "t", label: "Time", render: (r) => <span className="text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
            { key: "player_name", label: "Player" }, { key: "team_name", label: "Team" },
            { key: "amount", label: "Amount", render: (r) => <span className="num">{fmtL(r.amount)}</span> },
            { key: "status", label: "Status", render: (r) => <Badge className={BS[r.status]}>{r.status}</Badge> },
            { key: "reason", label: "Reason", render: (r) => <span className="text-xs text-slate-400">{r.reason || r.void_reason || ""}</span> },
            { key: "source", label: "Source" }, { key: "user_name", label: "User" },
          ]} />
        </TabsContent>
        <TabsContent value="sales">
          <DataTable rows={sales} testid="sales-table" columns={[
            { key: "t", label: "Time", render: (r) => <span className="text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
            { key: "player_name", label: "Player" }, { key: "team_name", label: "Team" },
            { key: "price", label: "Price", render: (r) => <span className="num">{fmtL(r.price)}</span> }, { key: "round", label: "Round" },
            { key: "source", label: "Type", render: (r) => <Badge className={r.source === "manual" ? "text-amber-300 border-amber-500/40" : "text-slate-400"}>{r.source === "manual" ? "manual" : "live"}</Badge> },
            { key: "status", label: "Status", render: (r) => <Badge className={r.status === "completed" ? BS.accepted : BS.voided}>{r.status}</Badge> },
            { key: "a", label: "", render: (r) => can("reverse_sale") && r.status === "completed" && <IconBtn title="Reverse sale" testid={`reverse-sale-${r.id}`} onClick={() => confirm({ title: `Reverse sale of ${r.player_name}?`, description: `Refunds ${fmtL(r.price)} to ${r.team_name}, removes the player from the squad and logs a reversal event.`, requireReason: true, destructive: true, confirmLabel: "Reverse sale", onConfirm: async (reason) => { try { await api.post(`/auction/${r.club_id}/reverse-sale`, { sale_id: r.id, reason }); toast.success("Sale reversed"); loadSales(); } catch (e) { toast.error(errMsg(e)); } } })}><Undo2 className="h-4 w-4" /></IconBtn> },
          ]} />
        </TabsContent>
        <TabsContent value="ledger">
          <Ex fn={exp("/ledger", "opl_ledger")} id="ledger" />
          <DataTable rows={ledger} testid="ledger-table" columns={[
            { key: "t", label: "Time", render: (r) => <span className="text-xs text-slate-400">{fmtTime(r.created_at)}</span> },
            { key: "event", label: "Event", render: (r) => <span className="num text-xs text-[#F5D061]">{r.event}</span> },
            { key: "amount", label: "Amount", render: (r) => <span className="num">{r.amount != null ? fmtL(r.amount) : "—"}</span> },
            { key: "reason", label: "Reason" },
          ]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
